"use client";

import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";

// 33 Bloor St E, Toronto, ON M4W 3H1
const LAT = 43.6697592;
const LNG = -79.3856383;

// Simple teardrop pin, drawn as an SVG string rather than pulling in
// Leaflet's bundled marker images (whose default paths break under most
// bundlers) — colour matches --px-text so it reads as part of the site,
// not the default Google-red pin.
const PIN_SVG = `
<svg width="28" height="38" viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg">
  <path d="M14 0C6.3 0 0 6.3 0 14c0 9.8 14 24 14 24s14-14.2 14-24C28 6.3 21.7 0 14 0z" fill="#1a1a1a"/>
  <circle cx="14" cy="14" r="5" fill="#ffffff"/>
</svg>`;

export default function ContactMap({ zoom = 15, style }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let cancelled = false;

    import("leaflet").then((mod) => {
      if (cancelled || mapRef.current) return;
      const L = mod.default || mod;

      const map = L.map(containerRef.current, {
        center: [LAT, LNG],
        zoom,
        scrollWheelZoom: false,
        attributionControl: false
      });
      mapRef.current = map;

      // Esri's "World Light Gray" canvas basemap: neutral greys instead of
      // Google's saturated roadmap colours, and just muted street names —
      // no POI/business icons — the combination the client asked for
      // (consistent palette, less clutter). Keyless, unlike CARTO's
      // basemaps.cartocdn.com, which now serves "API key required" tiles.
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 16
      }).addTo(map);

      L.control
        .attribution({ prefix: false, position: "bottomright" })
        .addAttribution("Esri, HERE, Garmin, © OpenStreetMap contributors")
        .addTo(map);

      L.marker([LAT, LNG], {
        icon: L.divIcon({ className: "px-map-pin", html: PIN_SVG, iconSize: [28, 38], iconAnchor: [14, 38] })
      }).addTo(map);
    });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [zoom]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%", ...style }} />;
}
